import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { Map, control, latLng, tileLayer } from 'leaflet';
import { HttpService, StateService } from '../../core/services/';
import { TranslationPipe } from '../../shared/pipes';
import { MapAutoRefresh } from './map-auto-refresh';
import { MapSettings } from './models';
import { LayerService, LocationService } from './services';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [RouterLink, LeafletModule, TranslationPipe],
  templateUrl: './map.component.html',
  styleUrl: 'map.component.scss',
})
export class MapComponent {
  private map: Map | null = null;
  private mapSettings: MapSettings | undefined = undefined;
  private readonly mapAutoRefresh: MapAutoRefresh = new MapAutoRefresh();

  locationEnabled: boolean = false;

  options = {
    layers: [
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, minZoom: 9, detectRetina: true })
    ],
    zoom: 9,
    center: latLng(50.2, 15.7)
  };

  get layers() { return this.layerManager.layers };

  constructor(
    private readonly layerManager: LayerService,
    private readonly httpService: HttpService,
    private readonly locationService: LocationService,
    private readonly stateService: StateService) { }

  mapReady(map: Map) {
    this.map = map;

    this.httpService.getMap().subscribe(model => {
      this.mapSettings = new MapSettings(model);
      this.mapSettings.detailed = this.stateService.read<boolean>('detailed') ?? false;
      this.mapSettings.extraDetailed = this.stateService.read<boolean>('extraDetailed') ?? false;
      if (model.lat && model.lng)
        map.setView(latLng(model.lat, model.lng), model.initialZoom);

      map.zoomControl.remove();
      control.zoom({ position: 'topright' }).addTo(map);

      map.attributionControl.remove();
      map.addControl(control.attribution({ prefix: 'Leaflet' }));
      map.attributionControl.addAttribution('<a href="https://www.openstreetmap.org/copyright" target="blank">OpenStreetMap</a>')

      this.layerManager.init(map, this.mapSettings);

      this.mapAutoRefresh.init(model.refreshInterval, () => this.readViewPortData());
      this.readViewPortData(true);
    });
  }

  leafletMapMoveStart(_e: any) {
    this.layerManager.routeLayerService.clearArrows();
  }

  leafletMapMoveEnd(_e: any) {
    this.layerManager.contactPlacesLayerService.addContactPlaces();
    this.readViewPortData();
  }

  leafletClick(_e: any) {
    this.layerManager.deselectMarkers();
  }

  onDetailClick(_e: Event) {
    this.layerManager.changeDetail();
    this.stateService.store('detailed', this.mapSettings?.detailed);
    this.stateService.store('extraDetailed', this.mapSettings?.extraDetailed);
  }

  onLocationClick(_e: Event) {
    if (this.locationEnabled) {
      this.locationEnabled = false;
      this.locationService.removeLocation();
    }
    else {
      this.locationEnabled = this.locationService.getLocationAndMove();
    }
  }

  private readViewPortData(force: boolean = false) {
    if (force || ((!this.mapSettings?.isBlocked && this.mapAutoRefresh.getElapsedTime() > 500))) {
      this.mapAutoRefresh.setRefreshInterval();
      const bounds = this.map!.getBounds();
      this.httpService.getMapData(JSON.stringify({
        w: bounds.getWest(),
        s: bounds.getSouth(),
        e: bounds.getEast(),
        n: bounds.getNorth(),
        zoom: this.map?.getZoom()
      })).subscribe(model => {
        this.layerManager.redraw(model);
      });
    }
  }
}
